import { useSettingFoiForFdSequentialModals } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingFoiForFdSequentialModals";
import { isNotUndefined } from "@faire/typescript";
import { FeatureName } from "@faire/web-api--source/indigofair/data/FeatureName";
import { PromoModalType } from "@faire/web-api--source/indigofair/data/PromoModalType";
import { useCallback, useMemo } from "react";

import { ModalTypeOrFeature } from "./gates/type";

const CONDUCTOR_FEATURE_NAME_EXCEPTIONS = [
  FeatureName.HOLIDAY_SPEND,
  FeatureName.FAIRE_PAY_CAMPAIGN,
];

export const useGetHighestPriorityModalType = () => {
  const isFoiForFdSequentialModals = useSettingFoiForFdSequentialModals();

  // In general, the order of the list is not important because most of the modals come already in the correct order.
  // Exceptions are: conductor related modals, opening order modals
  const orderedList = useMemo(
    () =>
      [
        // Priority order based on useModalToDisplay function logic
        // Modals requiring user action
        PromoModalType.PAYMENT_DISPUTE_MODAL,
        PromoModalType.PAYMENT_PLAN_PAYMENT_FAILED_MODAL,
        PromoModalType.PAYMENT_PLAN_FULLY_PAID_MODAL,
        PromoModalType.FAIRE_PAY_DEFAULTED_MODAL,
        PromoModalType.INSIDER_TERMS_AND_CONDITIONS_ACCEPTANCE,
        PromoModalType.VENDOR_UPLOAD_LIST_MODAL,
        PromoModalType.VENDOR_UPLOAD_MATCHES_FOUND_MODAL,

        // Other time sensitive modals
        PromoModalType.UPDATE_BUSINESS_INFO_MODAL,
        PromoModalType.OPENING_ORDER_DISCOUNT_COUNTDOWN_MODAL,

        // Conductor exceptions
        ...CONDUCTOR_FEATURE_NAME_EXCEPTIONS,

        // Other modals
        PromoModalType.BRAND_CONSOLIDATION_PROMO_MODAL,
        isFoiForFdSequentialModals
          ? PromoModalType.FAIRE_DIRECT_INCENTIVE_MODAL
          : undefined,

        // Conductor Modals
        PromoModalType.CONDUCTOR_MODAL,
        ...Object.values(FeatureName).filter(
          (featureName) =>
            !CONDUCTOR_FEATURE_NAME_EXCEPTIONS.includes(featureName)
        ),
        // End of conductor modals

        PromoModalType.FIRST_ORDER_INCENTIVE_MODAL,
        isFoiForFdSequentialModals
          ? undefined
          : PromoModalType.FAIRE_DIRECT_INCENTIVE_MODAL,
        PromoModalType.FAIRE_DIRECT_MARKETPLACE_DISCOUNT_MODAL,
        PromoModalType.POST_ORDER_INSIDER_ENROLLMENT,
        PromoModalType.OPENING_ORDER_DISCOUNT_MODAL,
        PromoModalType.WAREHOUSE_SALE_MODAL,
        PromoModalType.ACH_PROACTIVE_LIMIT_INCREASE_LANDING_MODAL,
        PromoModalType.ACH_TERMS_PRE_APPROVAL_LANDING_MODAL,
        PromoModalType.TERMS_PRE_APPROVAL_LANDING_MODAL,
        PromoModalType.SHOPIFY_NEW_RETAILER_BENEFITS_MODAL,
        PromoModalType.SHOPIFY_EXISTING_RETAILER_BENEFITS_MODAL,
        PromoModalType.NON_BM_INITIAL_LIMIT_LANDING_MODAL,
        PromoModalType.INSIDER_PARTNERSHIP_LAUNCH_ANNOUNCEMENT,
        PromoModalType.INSIDER_REFRESH_REMINDER_MODAL,
        PromoModalType.INSIDER_CAN_AUS_LAUNCH_MODAL,
        PromoModalType.VOLUME_PRICING_ANNOUNCEMENT_MODAL,
        PromoModalType.ONE_OFF_LIMIT_INCREASE_MODAL,
      ].filter(isNotUndefined),
    [isFoiForFdSequentialModals]
  );

  const getHighestPriorityModalType = useCallback(
    (modalTypes: (ModalTypeOrFeature | undefined)[]) => {
      if (!modalTypes.length) {
        return undefined;
      }

      return modalTypes.reduce((highestPriorityModalType, currentType) => {
        const currentIndex = orderedList.indexOf(currentType as PromoModalType);

        if (currentIndex === -1) {
          return highestPriorityModalType;
        }

        if (!highestPriorityModalType) {
          return currentType;
        }

        const highestPriorityIndex = orderedList.indexOf(
          highestPriorityModalType as PromoModalType
        );

        if (currentIndex < highestPriorityIndex) {
          return currentType;
        }

        return highestPriorityModalType;
      }, undefined);
    },
    [orderedList]
  );

  return { getHighestPriorityModalType };
};
